import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { FaRupeeSign, FaUniversity, FaCheckCircle, FaTimesCircle, FaClock, FaSpinner, FaUser, FaEnvelope, FaPhone, FaExclamationTriangle } from 'react-icons/fa';

const AdminPayouts = () => {
  const { user } = useAuth();
  const [riders, setRiders] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [selectedRider, setSelectedRider] = useState(null);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutNotes, setPayoutNotes] = useState('');
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchRiders();
    fetchPayouts();
    fetchStats();
  }, []);

  const fetchRiders = async () => {
    try {
      const response = await api.get('/api/admin/payouts/riders');
      setRiders(response.data.riders);
    } catch (error) {
      console.error('Fetch riders error:', error);
      toast.error('Failed to load riders');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayouts = async () => {
    try {
      const response = await api.get('/api/admin/payouts');
      setPayouts(response.data.payouts);
    } catch (error) {
      console.error('Fetch payouts error:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/admin/payouts/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Fetch stats error:', error);
    }
  };

  const handlePayoutClick = (rider) => {
    setSelectedRider(rider);
    setPayoutAmount(rider.walletBalance.toString());
    setShowPayoutModal(true);
  };

  const handleSinglePayout = async () => {
    if (!selectedRider || !payoutAmount || parseFloat(payoutAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setPayoutLoading(true);
    try {
      const response = await api.post('/api/admin/payouts', {
        riderId: selectedRider._id,
        amount: parseFloat(payoutAmount),
        notes: payoutNotes
      });

      toast.success(`Payout of ₹${payoutAmount} initiated for ${selectedRider.name}`);
      setShowPayoutModal(false);
      setPayoutAmount('');
      setPayoutNotes('');
      setSelectedRider(null);
      
      // Refresh data
      fetchRiders();
      fetchPayouts();
      fetchStats();
    } catch (error) {
      console.error('Payout error:', error);
      const message = error.response?.data?.message || 'Failed to process payout';
      toast.error(message);
    } finally {
      setPayoutLoading(false);
    }
  };

  const handleBulkPayout = async () => {
    if (!window.confirm('Are you sure you want to process payouts for all eligible riders? This action cannot be undone.')) {
      return;
    }

    setBulkLoading(true);
    try {
      const response = await api.post('/api/admin/payouts/bulk', {
        notes: payoutNotes || `Bulk payout - ${new Date().toLocaleDateString()}`
      });

      const { results } = response.data;
      toast.success(`Processed ${results.successful.length} payouts. Total: ₹${results.totalAmount.toFixed(2)}`);
      
      if (results.failed.length > 0) {
        toast.error(`${results.failed.length} payouts failed`);
      }

      // Refresh data
      fetchRiders();
      fetchPayouts();
      fetchStats();
    } catch (error) {
      console.error('Bulk payout error:', error);
      const message = error.response?.data?.message || 'Failed to process bulk payout';
      toast.error(message);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleApproveWithdrawal = async (payoutId) => {
    try {
      const response = await api.put(`/api/admin/payouts/${payoutId}/approve`);
      toast.success('Withdrawal request approved');
      fetchRiders();
      fetchPayouts();
      fetchStats();
    } catch (error) {
      console.error('Approve withdrawal error:', error);
      const message = error.response?.data?.message || 'Failed to approve withdrawal';
      toast.error(message);
    }
  };

  const handleRejectWithdrawal = async (payoutId) => {
    const reason = window.prompt('Enter rejection reason (optional):');
    if (reason === null) return; // User cancelled

    try {
      const response = await api.put(`/api/admin/payouts/${payoutId}/reject`, { reason });
      toast.success('Withdrawal request rejected');
      fetchRiders();
      fetchPayouts();
      fetchStats();
    } catch (error) {
      console.error('Reject withdrawal error:', error);
      const message = error.response?.data?.message || 'Failed to reject withdrawal';
      toast.error(message);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      requested: 'badge-warning',
      pending: 'badge-warning',
      processing: 'badge-info',
      completed: 'badge-success',
      failed: 'badge-danger',
      cancelled: 'badge-secondary',
      rejected: 'badge-danger'
    };

    const icons = {
      requested: <FaClock className="inline mr-1" />,
      pending: <FaClock className="inline mr-1" />,
      processing: <FaSpinner className="inline mr-1 animate-spin" />,
      completed: <FaCheckCircle className="inline mr-1" />,
      failed: <FaTimesCircle className="inline mr-1" />,
      cancelled: <FaTimesCircle className="inline mr-1" />,
      rejected: <FaTimesCircle className="inline mr-1" />
    };

    return (
      <span className={`badge ${badges[status] || 'badge-secondary'}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const eligibleRiders = riders.filter(r => r.walletBalance > 0 && r.hasBankDetails);
  const totalPendingAmount = eligibleRiders.reduce((sum, r) => sum + r.walletBalance, 0);

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Payout Management</h1>
            <p className="text-gray-600">Manage rider payouts and bank transfers</p>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
              <div className="card bg-blue-50">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-1">Total Payouts</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.totals?.total || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">₹{stats.totals?.total?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
              <div className="card bg-yellow-50">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-1">Requested</p>
                  <p className="text-2xl font-bold text-yellow-600">₹{stats.totals?.requested?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
              <div className="card bg-green-50">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-1">Completed</p>
                  <p className="text-2xl font-bold text-green-600">₹{stats.totals?.completed?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
              <div className="card bg-orange-50">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-1">Pending</p>
                  <p className="text-2xl font-bold text-orange-600">₹{stats.totals?.pending?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
              <div className="card bg-purple-50">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-1">Processing</p>
                  <p className="text-2xl font-bold text-purple-600">₹{stats.totals?.processing?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Bulk Actions */}
          <div className="card mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Bulk Actions</h3>
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">
                  Eligible Riders: <span className="font-semibold text-blue-600">{eligibleRiders.length}</span>
                </p>
                <p className="text-sm text-gray-600">
                  Total Pending Amount: <span className="font-semibold text-green-600">₹{totalPendingAmount.toFixed(2)}</span>
                </p>
              </div>
              <button
                onClick={handleBulkPayout}
                disabled={bulkLoading || eligibleRiders.length === 0}
                className="btn-primary"
              >
                {bulkLoading ? (
                  <>
                    <FaSpinner className="inline mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <FaRupeeSign className="inline mr-2" />
                    Payout All Eligible Riders
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Withdrawal Requests Section */}
          <div className="card mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaExclamationTriangle className="mr-2 text-yellow-600" />
              Withdrawal Requests ({payouts.filter(p => p.status === 'requested').length})
            </h3>
            {payouts.filter(p => p.status === 'requested').length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Rider</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Transaction ID</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Amount</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Bank Details</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Requested</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.filter(p => p.status === 'requested').map((payout) => (
                      <tr key={payout._id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-4 px-4">
                          <div>
                            <p className="font-medium">{payout.rider?.name}</p>
                            <p className="text-sm text-gray-600">{payout.rider?.email}</p>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <p className="text-sm font-mono text-gray-600">{payout.transactionId}</p>
                        </td>
                        <td className="py-4 px-4">
                          <span className="font-semibold text-green-600">₹{payout.amount.toFixed(2)}</span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="text-sm">
                            <p>{payout.bankDetails?.bankName}</p>
                            <p className="text-gray-600">{payout.bankDetails?.ifscCode}</p>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <p className="text-sm text-gray-600">
                            {new Date(payout.createdAt).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(payout.createdAt).toLocaleTimeString()}
                          </p>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleApproveWithdrawal(payout._id)}
                              className="btn-primary text-sm"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleRejectWithdrawal(payout._id)}
                              className="btn-secondary text-sm bg-red-600 hover:bg-red-700 text-white"
                            >
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No pending withdrawal requests</p>
            )}
          </div>

          {/* Riders List */}
          <div className="card mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Riders</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Rider</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Wallet Balance</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Bank Details</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {riders.map((rider) => (
                    <tr key={rider._id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-4">
                        <div>
                          <p className="font-medium text-gray-800">{rider.name}</p>
                          <p className="text-sm text-gray-600 flex items-center mt-1">
                            <FaEnvelope className="mr-1 text-xs" />
                            {rider.email}
                          </p>
                          <p className="text-sm text-gray-600 flex items-center mt-1">
                            <FaPhone className="mr-1 text-xs" />
                            {rider.phone}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`font-semibold ${rider.walletBalance > 0 ? 'text-green-600' : 'text-gray-600'}`}>
                          ₹{rider.walletBalance.toFixed(2)}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        {rider.hasBankDetails ? (
                          <div className="text-sm">
                            <div className="flex items-center text-green-600">
                              <FaCheckCircle className="mr-1" />
                              <span>Configured</span>
                            </div>
                            <p className="text-xs text-gray-600 mt-1">
                              {rider.bankDetails?.bankName} • {rider.bankDetails?.ifscCode}
                            </p>
                          </div>
                        ) : (
                          <span className="text-yellow-600 text-sm flex items-center">
                            <FaTimesCircle className="mr-1" />
                            Not Configured
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <button
                          onClick={() => handlePayoutClick(rider)}
                          disabled={rider.walletBalance <= 0 || !rider.hasBankDetails}
                          className={`btn-primary text-sm ${
                            rider.walletBalance <= 0 || !rider.hasBankDetails
                              ? 'opacity-50 cursor-not-allowed'
                              : ''
                          }`}
                        >
                          <FaRupeeSign className="inline mr-1" />
                          Payout
                        </button>
                      </td>
                    </tr>
                  ))}
                  {riders.length === 0 && (
                    <tr>
                      <td colSpan="4" className="text-center py-8 text-gray-500">
                        No riders found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Payouts */}
          <div className="card">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Recent Payouts</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Transaction ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Rider</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Amount</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.slice(0, 10).map((payout) => (
                    <tr key={payout._id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-4">
                        <p className="text-sm font-mono text-gray-600">{payout.transactionId}</p>
                      </td>
                      <td className="py-4 px-4">
                        <p className="font-medium">{payout.rider?.name}</p>
                        <p className="text-sm text-gray-600">{payout.rider?.email}</p>
                      </td>
                      <td className="py-4 px-4">
                        <span className="font-semibold text-green-600">₹{payout.amount.toFixed(2)}</span>
                      </td>
                      <td className="py-4 px-4">
                        {getStatusBadge(payout.status)}
                      </td>
                      <td className="py-4 px-4">
                        <p className="text-sm text-gray-600">
                          {new Date(payout.createdAt).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(payout.createdAt).toLocaleTimeString()}
                        </p>
                      </td>
                    </tr>
                  ))}
                  {payouts.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center py-8 text-gray-500">
                        No payouts found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Payout Modal */}
      {showPayoutModal && selectedRider && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Process Payout</h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Rider:</p>
              <p className="font-medium">{selectedRider.name}</p>
              <p className="text-sm text-gray-600">{selectedRider.email}</p>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Bank Details:</p>
              <div className="bg-gray-50 p-3 rounded text-sm">
                <p><strong>Account:</strong> {selectedRider.bankDetails?.accountNumber}</p>
                <p><strong>IFSC:</strong> {selectedRider.bankDetails?.ifscCode}</p>
                <p><strong>Bank:</strong> {selectedRider.bankDetails?.bankName}</p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount (Max: ₹{selectedRider.walletBalance.toFixed(2)})
              </label>
              <input
                type="number"
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
                max={selectedRider.walletBalance}
                min={1}
                step="0.01"
                className="input-field"
                placeholder="Enter amount"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={payoutNotes}
                onChange={(e) => setPayoutNotes(e.target.value)}
                className="input-field"
                rows="3"
                placeholder="Add notes..."
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPayoutModal(false);
                  setSelectedRider(null);
                  setPayoutAmount('');
                  setPayoutNotes('');
                }}
                className="btn-secondary flex-1"
                disabled={payoutLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleSinglePayout}
                disabled={payoutLoading || !payoutAmount || parseFloat(payoutAmount) <= 0}
                className="btn-primary flex-1"
              >
                {payoutLoading ? (
                  <>
                    <FaSpinner className="inline mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Process Payout'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPayouts;

